'use client';

import React, { useState, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { CountBadge } from './count-badge';
import { Button, type ButtonProps } from './button';
import type { ControlDensity, ControlSize } from './control-styles';

export interface CollapsibleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerClassName?: string;
  contentClassName?: string;
  icon?: React.ReactNode;
  summary?: React.ReactNode;
  /** Existing compact summaries may remain visible while the body is collapsed. */
  preview?: React.ReactNode;
  count?: number | null;
  disabled?: boolean;
  /** Section disclosures use a heading; compact action menus do not. */
  heading?: boolean;
  /** Keep form/portal state mounted unless the caller explicitly opts out. */
  keepMounted?: boolean;
  /** Action disclosures share Button geometry; section headings retain their own anatomy. */
  triggerVariant?: ButtonProps['variant'];
  size?: ControlSize;
  density?: ControlDensity;
}

/** Shared disclosure anatomy: 44px title/summary row and one trailing chevron. */
export const Collapsible = React.forwardRef<HTMLDivElement, CollapsibleProps>(function Collapsible(
  {
    label,
    children,
    defaultOpen = false,
    open,
    onOpenChange,
    className,
    triggerClassName,
    contentClassName,
    icon,
    summary,
    preview,
    count,
    disabled,
    heading = false,
    keepMounted = true,
    triggerVariant,
    size = 'default',
    density,
    ...props
  },
  ref,
) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const expanded = open ?? internalOpen;
  const id = useId();
  const triggerProps = {
    type: 'button' as const,
    id: `${id}-title`,
    disabled,
    'aria-expanded': expanded,
    'aria-controls': id,
    onClick: () => {
      if (open === undefined) setInternalOpen(!expanded);
      onOpenChange?.(!expanded);
    },
  };
  const triggerContent = (
    <>
      {icon}
      <span className="flex min-w-0 flex-1 flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
        <span className="min-w-0 flex-1 break-normal [overflow-wrap:normal]">{label}</span>{' '}
        {(summary != null || count !== undefined) && (
          <span className="min-w-0 break-normal text-body font-normal text-text-muted [overflow-wrap:normal]">
            {count !== undefined ? (
              <CountBadge
                count={count}
                aria-label={String(count ?? 'Unknown')}
                label={`${typeof label === 'string' ? label : 'Section'} items`}
              />
            ) : (
              summary
            )}
          </span>
        )}
      </span>
      <ChevronDown aria-hidden="true" className={cn('size-4 shrink-0', expanded && 'rotate-180')} />
    </>
  );
  const trigger = triggerVariant ? (
    <Button
      {...triggerProps}
      variant={triggerVariant}
      size={size}
      density={density}
      className={triggerClassName}
    >
      {triggerContent}
    </Button>
  ) : (
    <button
      {...triggerProps}
      data-disclosure-trigger="section"
      className={cn(
        'flex min-h-11 w-full min-w-0 flex-wrap items-center gap-2 py-2 text-left font-body font-semibold text-text-heading focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        !expanded && 'font-medium',
        heading ? 'text-section-title' : 'text-card-title',
        triggerClassName,
      )}
    >
      {triggerContent}
    </button>
  );
  return (
    <div
      {...props}
      ref={ref}
      data-disclosure
      aria-labelledby={`${id}-title`}
      className={cn('min-w-0 rounded-card border border-border bg-surface-sheet px-4', className)}
    >
      {heading ? <h2>{trigger}</h2> : trigger}
      {preview}
      <div
        id={id}
        hidden={!expanded}
        className={cn(
          'min-w-0 break-words border-t border-border py-4 [overflow-wrap:anywhere]',
          contentClassName,
        )}
      >
        {keepMounted || expanded ? children : null}
      </div>
    </div>
  );
});
